import { useEffect, useState, type ReactNode } from "react";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { getMsal, ensureMsalInitialized, login } from "@/auth/msal";
import { config } from "@/lib/config";

function AuthGate({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      login().catch(() => {});
    }
  }, [isAuthenticated, inProgress]);

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Redirecting to sign-in…
      </div>
    );
  }

  return <>{children}</>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(!config.isAuthEnabled());

  useEffect(() => {
    if (!config.isAuthEnabled()) return;
    ensureMsalInitialized().finally(() => setReady(true));
  }, []);

  if (!config.isAuthEnabled()) {
    return <>{children}</>;
  }

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        Initializing sign-in…
      </div>
    );
  }

  return (
    <MsalProvider instance={getMsal()}>
      <AuthGate>{children}</AuthGate>
    </MsalProvider>
  );
}
